import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type DragEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { isFreeTemplateCode, isFreeVersion, isReportManager, logoOf, useAttachments, useCollaboration, useReport, useReportMutations, useReportPulse, useTemplateVersion } from '../api/hooks'
import type { FindingDto, FormInstanceDto, HeaderField, PhotoDto, TemplateVersion } from '../api/types'
import { problemOf, type CurrentUser } from '../api/client'
import { useAuth } from '../auth/AuthProvider'
import { Badge, Empty, Modal, RevBadge, SecureImage, StatusBadge } from '../components/ui'
import DateInput from '../components/DateInput'
import CollaborationPanel from '../components/CollaborationPanel'
import { LogoField, NO_LOGO_CHANGE, type LogoChoice } from '../components/LogoControl'

type Mutations = ReturnType<typeof useReportMutations>
type SaveState = 'idle' | 'saving' | 'saved' | 'error'
/** What the page needs from a card before finishing or deleting: push the pending note, or drop it. */
interface NoteHandle { flush: () => Promise<boolean>; cancel: () => void; resume: () => void }

const pad2 = (n: number) => String(n).padStart(2, '0')
const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}` }
const isImage = (f: File) => f.type.startsWith('image/') || (f.type === '' && /\.(jpe?g|png|gif|webp|heic|heif|bmp)$/i.test(f.name))
const isEmptyItem = (item: FindingDto, photos: PhotoDto[]) => item.description.trim() === '' && photos.length === 0

/**
 * "Boş Rapor": a report without questions. Its content is a list of numbered items (note + photos) stored as findings
 * without a question; the item number is the position in sortOrder order. The header is optional and asked when finishing.
 * Shared reports: approved participants add and edit items next to the owner; the page polls the report's pulse and re-loads
 * when somebody else changed something. Only the owner (or a tenant admin) finishes or revises the report.
 */
export default function FreeReportPage() {
  const { id = '' } = useParams()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const report = useReport(id)
  const version = useTemplateVersion(report.data?.templateVersionId)
  const m = useReportMutations(id)
  const [lightbox, setLightbox] = useState<string | null>(null)
  const [focusId, setFocusId] = useState<string | null>(null)
  const [finishOpen, setFinishOpen] = useState(false)
  const [adding, setAdding] = useState(false)
  const [uploads, setUploads] = useState(0)
  const [preparing, setPreparing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const handles = useRef(new Map<string, NoteHandle>())
  const autoCreated = useRef(false)

  const r = report.data
  const v = version.data
  const canEdit = !!user?.roles.some((x) => ['SuperAdmin', 'TenantAdmin', 'Auditor'].includes(x.role))
  const isFree = !!r && (isFreeTemplateCode(r.templateCode) || isFreeVersion(v))
  const editable = !!r && isFree && r.status === 'Draft' && canEdit
  // Completing, revising and the header belong to the report's owner (or a tenant admin); participants add and edit content.
  const manager = canEdit && isReportManager(user, r)
  const isCreator = !!r && (!r.ownerUserId || r.ownerUserId === user?.id)
  // An item (note + photos) is one unit owned by whoever added it: the owner may fix anybody's, participants only their own.
  const canEditItem = (item: FindingDto) => editable && (manager || !item.createdBy || item.createdBy === user?.id)
  const pulse = useReportPulse(id, !!r && isFree)
  const collab = useCollaboration(id, !!r)
  const authors = useMemo(() => new Map((collab.data?.people ?? []).map((p) => [p.userId, p.fullName])), [collab.data])
  const shared = (r?.collaboratorCount ?? 0) > 0 || (collab.data?.people ?? []).some((p) => p.role === 'Collaborator' && p.status === 'Approved')

  const items = useMemo(() => (r?.findings ?? []).filter((f) => !f.questionId).sort((a, b) => a.sortOrder - b.sortOrder), [r?.findings])
  const photosByItem = useMemo(() => {
    const map = new Map<string, PhotoDto[]>()
    for (const p of [...(r?.photos ?? [])].sort((a, b) => a.sortOrder - b.sortOrder)) {
      if (!p.findingId) continue
      map.set(p.findingId, [...(map.get(p.findingId) ?? []), p])
    }
    return map
  }, [r?.photos])

  const { mutateAsync: saveItem } = m.saveItem
  const addItem = useCallback(async () => {
    setError(null)
    setAdding(true)
    try {
      // No sortOrder: the server appends after the last item, so numbering stays right when several people add at once.
      const created = await saveItem({ clientId: crypto.randomUUID(), severity: 'Minor', description: '', openAction: false })
      setFocusId(created.id)
    } catch (e) {
      setError(problemOf(e).title ?? t('common.error'))
    } finally {
      setAdding(false)
    }
  }, [saveItem, t])

  // An empty report starts with item 1 ready to type into (for its creator; participants add their own when they need one).
  useEffect(() => {
    if (!editable || !isCreator || autoCreated.current || report.isFetching) return
    autoCreated.current = true
    if (items.length === 0) void addItem()
  }, [editable, isCreator, report.isFetching, items.length, addItem])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setLightbox(null)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const register = useCallback((itemId: string, handle: NoteHandle | null) => {
    if (handle) handles.current.set(itemId, handle)
    else handles.current.delete(itemId)
  }, [])
  const clearFocus = useCallback(() => setFocusId(null), [])
  const onUploading = useCallback((delta: 1 | -1) => setUploads((n) => Math.max(0, n + delta)), [])

  if (report.isLoading || (r && version.isLoading)) return <p>{t('common.loading')}</p>
  if (!r) return <p className="error">{t('common.loadFailed')}</p>
  if (!isFree) return <Navigate to={r.status === 'Draft' ? `/reports/${id}/fill` : `/reports/${id}`} replace />
  if (pulse.lost) return <div className="card stack"><p style={{ margin: 0 }}>{t('collab.accessLost')}</p><div><Link className="button" to="/reports">{t('nav.reports')}</Link></div></div>

  function move(index: number, dir: -1 | 1) {
    const j = index + dir
    if (j < 0 || j >= items.length) return
    const ids = items.map((x) => x.id)
    ;[ids[index], ids[j]] = [ids[j], ids[index]]
    setError(null)
    m.reorderFindings.mutate(ids, { onError: (e) => setError(problemOf(e).title ?? t('common.error')) })
  }

  async function removeItem(item: FindingDto, no: number) {
    if (!confirm(t('freeReport.deleteItemConfirm', { no }))) return
    setError(null)
    handles.current.get(item.id)?.cancel()
    try {
      // A free-form item owns its photos: the API removes them together with the item.
      await m.deleteFinding.mutateAsync(item.id)
    } catch (e) {
      handles.current.get(item.id)?.resume() // the item is still there: keep saving its note
      setError(problemOf(e).title ?? t('common.error'))
    }
  }

  async function openFinish() {
    setError(null)
    setPreparing(true)
    try {
      const results = await Promise.all([...handles.current.values()].map((h) => h.flush()))
      if (results.includes(false)) { setError(t('freeReport.notesNotSaved')); return }
      // Finishing drops empty items, so it must decide on what the server has, not on a cache an older response may have overwritten.
      const fresh = await report.refetch()
      if (fresh.isError) { setError(problemOf(fresh.error).title ?? t('common.loadFailed')); return }
      setFinishOpen(true)
    } finally {
      setPreparing(false)
    }
  }
  const finishDisabled = preparing || uploads > 0 || m.complete.isPending

  const busy = m.deleteFinding.isPending || m.reorderFindings.isPending
  const ownerName = collab.data?.ownerName ?? (r.ownerUserId ? authors.get(r.ownerUserId) : undefined)

  return (
    <div className="free-page">
      <div className="free-topbar row between wrap">
        <div>
          <div className="row wrap" style={{ gap: 8 }}>
            <h2 style={{ margin: 0 }}>{t('freeReport.title')}</h2>
            <StatusBadge status={r.status} />
            <RevBadge revisionNo={r.revisionNo} />
            {shared ? <Badge color="#1d6f42" title={t('collab.sharedHint')}>{t('collab.shared')}</Badge> : null}
            <Badge>{t('freeReport.itemCount', { count: items.length })}</Badge>
          </div>
          <div className="muted small">{r.code ? `${r.code} · ` : ''}{r.companyName}</div>
        </div>
        <div className="row wrap">
          <Link className="button secondary" to={`/reports/${id}`}>{t('reports.open')}</Link>
          {editable && manager ? <button onClick={() => void openFinish()} disabled={finishDisabled}>{t('freeReport.finish')}</button> : null}
          {!editable && r.status === 'Completed' && manager ? <button onClick={() => confirm(t('revise.confirm', { n: r.revisionNo + 1 })) && m.reopen.mutate()} disabled={m.reopen.isPending}>{t('revise.action')}</button> : null}
        </div>
      </div>

      {!editable ? <p className="muted small" style={{ margin: 0 }}>{r.status === 'Draft' ? t('freeReport.viewOnly') : manager ? t('revise.readOnly') : t('revise.readOnlyParticipant')}</p> : null}
      {editable && !manager ? <p className="banner" role="note">{ownerName ? t('collab.participantBanner', { name: ownerName }) : t('collab.participantBannerNoName')}</p> : null}
      {m.reopen.isError ? <p className="error small" style={{ margin: 0 }}>{problemOf(m.reopen.error).title ?? t('common.error')}</p> : null}
      {error ? <p className="error small" role="alert" style={{ margin: 0 }}>{error}</p> : null}

      <CollaborationPanel reportId={id} status={r.status} live={pulse.live && r.status === 'Draft'} />

      {items.length === 0 ? <div className="card"><Empty text={adding ? t('common.pleaseWait') : t('freeReport.noItems')} /></div> : null}
      <div className="free-items">
        {items.map((item, i) => (
          <ItemCard
            key={item.id}
            item={item}
            no={i + 1}
            isFirst={i === 0}
            isLast={i === items.length - 1}
            photos={photosByItem.get(item.id) ?? []}
            author={shared && item.createdBy ? authors.get(item.createdBy) : undefined}
            editable={canEditItem(item)}
            locked={editable && !canEditItem(item)}
            canReorder={manager}
            busy={busy}
            m={m}
            focus={focusId === item.id}
            onFocused={clearFocus}
            register={register}
            onUploading={onUploading}
            onMove={(dir) => move(i, dir)}
            onDelete={() => void removeItem(item, i + 1)}
            onLightbox={setLightbox}
          />
        ))}
      </div>

      {editable ? (
        <div className="free-bottom">
          <button className="secondary" onClick={() => void addItem()} disabled={adding}>{adding ? t('common.pleaseWait') : t('freeReport.addItem')}</button>
          {manager ? <button onClick={() => void openFinish()} disabled={finishDisabled}>{uploads > 0 ? t('freeReport.waitUploads') : t('freeReport.finish')}</button> : null}
        </div>
      ) : null}

      {finishOpen && v && manager ? (
        <FinishModal r={r} v={v} user={user} items={items} photosByItem={photosByItem} m={m} onClose={() => setFinishOpen(false)} onDone={() => navigate(`/reports/${id}`)} />
      ) : null}
      {lightbox ? <div className="modal-backdrop" onClick={() => setLightbox(null)}><SecureImage fileId={lightbox} alt="" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 8 }} /></div> : null}
    </div>
  )
}

/**
 * Note text with autosave: 800 ms after the last keystroke and on blur. Saves run one at a time, always with the latest text.
 * The report is re-loaded while other people work on it, so the server's text is adopted too — but never over what the user is
 * typing: not while the field has focus, not while a save is pending or running, and not from a snapshot older than the last own save.
 */
function useNoteAutosave(server: string, serverUpdatedAt: string | undefined, save: (value: string) => Promise<{ updatedAt?: string } | void>) {
  const [text, setText] = useState(server)
  const [state, setState] = useState<SaveState>('idle')
  const latest = useRef(server)
  const saved = useRef(server)
  const running = useRef<Promise<boolean> | null>(null)
  const timer = useRef<number | null>(null)
  const cancelled = useRef(false)
  const focused = useRef(false)
  const savedAt = useRef(0) // server time of the last own save
  const remote = useRef({ text: server, at: serverUpdatedAt })
  const saveRef = useRef(save)
  useEffect(() => { saveRef.current = save }, [save])

  const adopt = useCallback(() => {
    if (focused.current || timer.current || running.current || latest.current !== saved.current) return
    const next = remote.current
    if (next.text === latest.current) return
    if ((next.at ? Date.parse(next.at) || 0 : 0) < savedAt.current) return // a load that started before the last own save
    latest.current = next.text
    saved.current = next.text
    setText(next.text)
    setState('idle')
  }, [])

  useEffect(() => {
    remote.current = { text: server, at: serverUpdatedAt }
    adopt()
  }, [server, serverUpdatedAt, adopt])

  const flush = useCallback((): Promise<boolean> => {
    if (timer.current) { window.clearTimeout(timer.current); timer.current = null }
    if (running.current) return running.current // the running loop picks up the latest text itself
    if (cancelled.current || latest.current === saved.current) return Promise.resolve(true)
    const run = (async () => {
      try {
        while (!cancelled.current && latest.current !== saved.current) {
          const value = latest.current
          setState('saving')
          const result = await saveRef.current(value)
          saved.current = value
          if (result?.updatedAt) savedAt.current = Math.max(savedAt.current, Date.parse(result.updatedAt) || 0)
        }
        setState('saved')
        return true
      } catch {
        setState('error')
        return false
      } finally {
        running.current = null
      }
    })()
    running.current = run
    return run
  }, [])

  const change = useCallback((value: string) => {
    setText(value)
    latest.current = value
    if (timer.current) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => void flush(), 800)
  }, [flush])

  const onFocus = useCallback(() => { focused.current = true }, [])
  // Leaving the field: save what was typed, then catch up with whatever arrived from others meanwhile.
  const onBlur = useCallback(() => {
    focused.current = false
    void flush().then(adopt)
  }, [flush, adopt])

  const cancel = useCallback(() => {
    cancelled.current = true
    if (timer.current) { window.clearTimeout(timer.current); timer.current = null }
  }, [])

  const resume = useCallback(() => { cancelled.current = false }, [])

  // Leaving the page with an unsaved note: push it out instead of dropping it.
  useEffect(() => () => { void flush() }, [flush])

  return { text, state, change, flush, cancel, resume, onFocus, onBlur }
}

function ItemCard({ item, no, isFirst, isLast, photos, author, editable, locked, canReorder, busy, m, focus, onFocused, register, onUploading, onMove, onDelete, onLightbox }: {
  item: FindingDto; no: number; isFirst: boolean; isLast: boolean; photos: PhotoDto[]; author?: string; editable: boolean
  /** Somebody else's item in a report this user is editing: shown read-only with a hint. */
  locked: boolean; canReorder: boolean; busy: boolean; m: Mutations
  focus: boolean; onFocused: () => void; register: (itemId: string, handle: NoteHandle | null) => void; onUploading: (delta: 1 | -1) => void
  onMove: (dir: -1 | 1) => void; onDelete: () => void; onLightbox: (fileId: string) => void
}) {
  const { t } = useTranslation()
  const { mutateAsync: saveItem } = m.saveItem
  const { mutateAsync: uploadPhoto } = m.uploadPhoto
  // sortOrder is left out on purpose: the order belongs to the ↑/↓ buttons, a late note save must never undo a move.
  const save = useCallback((value: string) => saveItem({ id: item.id, severity: 'Minor', description: value, openAction: false }), [saveItem, item.id])
  const note = useNoteAutosave(item.description, item.updatedAt, save)
  const [drag, setDrag] = useState(false)
  const [uploading, setUploading] = useState<{ done: number; total: number } | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const cardRef = useRef<HTMLElement>(null)
  const noteRef = useRef<HTMLTextAreaElement>(null)

  const { flush, cancel, resume } = note
  useEffect(() => {
    register(item.id, { flush, cancel, resume })
    return () => register(item.id, null)
  }, [register, item.id, flush, cancel, resume])

  useEffect(() => {
    if (!focus) return
    cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    noteRef.current?.focus({ preventScroll: true })
    onFocused()
  }, [focus, onFocused])

  async function upload(files: File[]) {
    const list = files.filter(isImage)
    if (list.length === 0) { if (files.length > 0) setUploadError(t('freeReport.onlyImages')); return }
    setUploadError(null)
    setUploading({ done: 0, total: list.length })
    onUploading(1)
    let order = photos.reduce((max, p) => Math.max(max, p.sortOrder), 0)
    const failed: string[] = []
    for (const [i, file] of list.entries()) {
      try {
        await uploadPhoto({ file, findingId: item.id, sortOrder: ++order })
      } catch (e) {
        failed.push(`${file.name}: ${problemOf(e).title ?? t('freeReport.uploadFailed')}`)
      }
      setUploading({ done: i + 1, total: list.length })
    }
    setUploading(null)
    onUploading(-1)
    if (failed.length > 0) setUploadError(failed.join(' · '))
  }
  function onPick(e: ChangeEvent<HTMLInputElement>) {
    const files = [...(e.target.files ?? [])]
    e.target.value = '' // so the same photo can be picked again
    void upload(files)
  }
  function onDrop(e: DragEvent) {
    e.preventDefault()
    setDrag(false)
    void upload([...e.dataTransfer.files])
  }

  const thumbs = photos.length > 0 ? (
    <div className="free-photos">
      {photos.map((p) => (
        <figure key={p.id} className="free-photo">
          <SecureImage fileId={p.thumbnailFileId ?? p.fileId} alt={p.caption ?? ''} className="thumb" onClick={() => onLightbox(p.fileId)} />
          {editable ? (
            <button type="button" className="free-photo-delete" onClick={() => confirm(t('freeReport.deletePhotoConfirm')) && m.deletePhoto.mutate(p.id)} aria-label={t('freeReport.deletePhoto')} title={t('freeReport.deletePhoto')}>×</button>
          ) : null}
        </figure>
      ))}
    </div>
  ) : null

  const authorLine = author ? <span className="muted small free-author">{t('collab.addedBy', { name: author })}</span> : null

  if (!editable) {
    return (
      <article className="card free-item readonly" ref={cardRef}>
        <span className="free-no" aria-label={`${t('reports.item')} ${item.number ?? no}`}>{item.number ?? no}</span>
        <div className="free-item-body">
          {item.description.trim() ? <p className="free-note">{item.description}</p> : <p className="muted free-note">{t('freeReport.noNote')}</p>}
          {thumbs}
          {authorLine}
          {locked ? <p className="muted small" style={{ margin: 0 }}>{author ? t('freeReport.othersItem', { name: author }) : t('freeReport.othersItemNoName')}</p> : null}
        </div>
      </article>
    )
  }

  return (
    <article className="card free-item" ref={cardRef}>
      <div className="free-item-side">
        <span className="free-no" aria-label={`${t('reports.item')} ${no}`}>{no}</span>
        <div className="free-item-tools">
          <button type="button" className="ghost" onClick={() => onMove(-1)} disabled={!canReorder || isFirst || busy} aria-label={t('freeReport.moveUp')} title={t('freeReport.moveUp')}>↑</button>
          <button type="button" className="ghost" onClick={() => onMove(1)} disabled={!canReorder || isLast || busy} aria-label={t('freeReport.moveDown')} title={t('freeReport.moveDown')}>↓</button>
          <button type="button" className="ghost free-delete" onClick={onDelete} disabled={busy || !!uploading} aria-label={t('freeReport.deleteItem')} title={t('freeReport.deleteItem')}>🗑</button>
        </div>
      </div>
      <div className="free-item-body">
        <label>
          <span className="row between">
            <span className="small">{t('freeReport.note')}</span>
            <span className={`small free-save ${note.state === 'error' ? 'error' : 'muted'}`} role="status">
              {note.state === 'saving' ? t('freeReport.saving') : note.state === 'saved' ? `✓ ${t('freeReport.saved')}` : note.state === 'error' ? t('freeReport.saveError') : ''}
            </span>
          </span>
          <textarea ref={noteRef} value={note.text} onChange={(e) => note.change(e.target.value)} onFocus={note.onFocus} onBlur={note.onBlur} rows={3} maxLength={8000} placeholder={t('freeReport.notePlaceholder')} />
        </label>
        {authorLine}

        <div className={`dropzone ${drag ? 'drag' : ''}`} onDragOver={(e) => { e.preventDefault(); setDrag(true) }} onDragLeave={() => setDrag(false)} onDrop={onDrop}>
          <div className="row between wrap">
            <span className="muted small">{uploading ? t('freeReport.uploading', uploading) : t('freeReport.dropHint')}</span>
            <div className="row wrap free-photo-buttons">
              <label className="button secondary small-btn">{t('freeReport.takePhoto')}<input type="file" accept="image/*" capture="environment" hidden disabled={!!uploading} onChange={onPick} /></label>
              <label className="button secondary small-btn">{t('freeReport.pickPhotos')}<input type="file" accept="image/*" multiple hidden disabled={!!uploading} onChange={onPick} /></label>
            </div>
          </div>
          {thumbs}
        </div>
        {uploadError ? <p className="error small" role="alert" style={{ margin: 0 }}>{uploadError}</p> : null}
      </div>
    </article>
  )
}

/** "Rapor bilgileri (isteğe bağlı)": asked at the end; every field may stay empty. */
function FinishModal({ r, v, user, items, photosByItem, m, onClose, onDone }: {
  r: FormInstanceDto; v: TemplateVersion; user: CurrentUser | null; items: FindingDto[]; photosByItem: Map<string, PhotoDto[]>; m: Mutations; onClose: () => void; onDone: () => void
}) {
  const { t } = useTranslation()
  const fields = useMemo(
    () => v.headerFields
      .filter((f) => !f.isInstanceCode && !f.readOnly && (f.isAuditorList || ['Text', 'MultilineText', 'Date', 'Number', 'Select'].includes(f.type)))
      .sort((a, b) => a.sortOrder - b.sortOrder),
    [v.headerFields],
  )
  const hasAuthorField = fields.some((f) => f.isAuditorList)
  const autoTitle = `${r.templateName} — ${r.companyName}`
  const [title, setTitle] = useState(r.title && r.title !== autoTitle ? r.title : '')
  const [preparedBy, setPreparedBy] = useState(() => r.auditors.map((a) => a.displayName).join(' / ') || user?.fullName || '')
  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {}
    for (const f of fields) {
      if (f.isAuditorList) continue
      const current = r.headerValues[f.key]
      initial[f.key] = typeof current === 'string' || typeof current === 'number' ? String(current) : f.type === 'Date' && f.isAuditDate ? todayIso() : ''
    }
    return initial
  })
  const [busy, setBusy] = useState<'skip' | 'save' | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Optional company logo for the PowerPoint file; uploaded (or removed) right before the report is completed.
  const attachments = useAttachments(r.id)
  const currentLogo = logoOf(attachments.data)
  const [logo, setLogo] = useState<LogoChoice>(NO_LOGO_CHANGE)
  const emptyItems = items.filter((x) => isEmptyItem(x, photosByItem.get(x.id) ?? []))
  const set = (key: string, value: string) => setValues((prev) => ({ ...prev, [key]: value }))
  const labelOf = (f: HeaderField) => t(`freeReport.field.${f.key}`, { defaultValue: f.label })

  async function finish(saveHeader: boolean) {
    setBusy(saveHeader ? 'save' : 'skip')
    setError(null)
    try {
      if (saveHeader) {
        const headerValues: Record<string, unknown> = { ...r.headerValues }
        for (const f of fields) {
          if (f.isAuditorList) continue
          const raw = (values[f.key] ?? '').trim()
          if (raw === '') delete headerValues[f.key]
          else headerValues[f.key] = f.type === 'Number' ? Number(raw) : raw
        }
        const names = preparedBy.split(/[,/]+/).map((n) => n.trim()).filter(Boolean)
        await m.updateHeader.mutateAsync({
          title: title.trim() || undefined,
          headerValues,
          auditors: names.length > 0 ? names.map((n, i) => ({ displayName: n, isLead: i === 0, userId: n === user?.fullName ? user?.id : undefined })) : undefined,
        })
      }
      // The logo is a choice of its own: it is applied with "Atla ve bitir" too. It must be on the server before the files are generated.
      if (logo.file) {
        await m.uploadLogo.mutateAsync(logo.file)
        setLogo(NO_LOGO_CHANGE) // a retry after a later failure must not upload it twice
      } else if (logo.removeCurrent && currentLogo) {
        await m.deleteAttachment.mutateAsync(currentLogo.id)
        setLogo(NO_LOGO_CHANGE)
      }
      // Items without a note and without a photo would only print an empty number.
      for (const item of emptyItems) await m.deleteFinding.mutateAsync(item.id)
      await m.complete.mutateAsync(true)
      onDone()
    } catch (e) {
      const problem = problemOf(e)
      setError(problem.title ?? t('common.error'))
      setBusy(null)
    }
  }

  return (
    <Modal title={t('freeReport.finishTitle')} onClose={busy ? () => undefined : onClose}>
      <div className="stack">
        <p className="muted small" style={{ margin: 0 }}>{t('freeReport.finishHint')}</p>
        <label>{t('freeReport.reportName')}
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={autoTitle} maxLength={300} autoComplete="off" />
        </label>
        {!hasAuthorField ? <label>{t('freeReport.preparedBy')}<input value={preparedBy} onChange={(e) => setPreparedBy(e.target.value)} placeholder="Ad Soyad / Ad Soyad" autoComplete="off" /></label> : null}
        {fields.map((f) => (
          <label key={f.key}>{f.isAuditorList ? t('freeReport.preparedBy') : labelOf(f)}
            {f.isAuditorList ? (
              <input value={preparedBy} onChange={(e) => setPreparedBy(e.target.value)} placeholder="Ad Soyad / Ad Soyad" autoComplete="off" />
            ) : f.type === 'MultilineText' ? (
              <textarea value={values[f.key] ?? ''} onChange={(e) => set(f.key, e.target.value)} rows={3} placeholder={f.placeholder ?? ''} />
            ) : f.type === 'Date' ? (
              <DateInput value={values[f.key] ?? ''} onChange={(iso) => set(f.key, iso)} />
            ) : (
              <input value={values[f.key] ?? ''} onChange={(e) => set(f.key, e.target.value)} placeholder={f.placeholder ?? ''} type={f.type === 'Number' ? 'number' : 'text'} autoComplete="off" />
            )}
          </label>
        ))}
        <LogoField current={currentLogo} value={logo} onChange={setLogo} disabled={!!busy} />
        {emptyItems.length > 0 ? <p className="muted small" style={{ margin: 0 }}>{t('freeReport.emptyItemsHint', { count: emptyItems.length })}</p> : null}
        {error ?<p className="error small" role="alert" style={{ margin: 0 }}>{error}</p> : null}
        <div className="free-finish-actions">
          <button className="ghost" onClick={onClose} disabled={!!busy}>{t('common.cancel')}</button>
          <button className="secondary" onClick={() => void finish(false)} disabled={!!busy}>{busy === 'skip' ? t('freeReport.finishing') : t('freeReport.skipAndFinish')}</button>
          <button onClick={() => void finish(true)} disabled={!!busy}>{busy === 'save' ? t('freeReport.finishing') : t('freeReport.saveAndFinish')}</button>
        </div>
      </div>
    </Modal>
  )
}
